// frontend/src/pages/freelancer/FreelancerServices.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";
import axios from "axios";
import "./freelancerStyles.css";

const toUserFriendly = (text) => {
  if (!text) return "";
  return text
    .replace(/[-_]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const normalizeCategoryValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getCategoryValue = (category) => {
  if (Array.isArray(category)) {
    return category[0] || "";
  }
  if (category && typeof category === "object") {
    return category.value || category.label || "";
  }
  return category || "";
};

function FreelancerServices() {
  const navigate = useNavigate();
  const location = useLocation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState([
    { value: "all", label: "All" },
  ]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);
  const [platformTemplates, setPlatformTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState("");
  const [templateSearchTerm, setTemplateSearchTerm] = useState("");
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState("all");
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [addingTemplates, setAddingTemplates] = useState(false);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isBoothRenterRoute = location.pathname.startsWith(
    "/dashboard/booth-renter",
  );
  const basePath = isBoothRenterRoute
    ? "/dashboard/booth-renter"
    : "/dashboard/freelancer";

  // Security: Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Security: Validate API base URL
  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error. Please contact support.");
      return null;
    }
    return baseURL;
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.get(`${baseURL}/api/staff/services`, { headers });
      setServices(res.data.services || []);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        setErrorMsg("Unable to load services. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, getApiBaseUrl, navigate]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // Fetch service categories based on business types
  useEffect(() => {
    const fetchCategories = async () => {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      try {
        // Fetch staff profile to get business types
        const profileResponse = await axios.get(
          `${baseURL}/api/freelancer/profile`,
          { headers },
        );
        const staff = profileResponse.data?.staff || {};
        const businessTypes = Array.isArray(staff.businessTypes)
          ? staff.businessTypes
          : [];

        // If businessTypes available, fetch categories for those types
        if (businessTypes.length > 0) {
          const params = new URLSearchParams();
          params.append("businessCategories", businessTypes.join(","));

          const categoriesResponse = await axios.get(
            `${baseURL}/api/platform-settings/service-categories?${params.toString()}`,
            { headers },
          );
          const fetchedCategories = categoriesResponse.data.categories || [];
          setCategories([{ value: "all", label: "All" }, ...fetchedCategories]);
        } else {
          // No business types, just use All
          setCategories([{ value: "all", label: "All" }]);
        }
      } catch (err) {
        // Keep default 'All' category on error
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, [getAuthHeaders, getApiBaseUrl]);

  const fetchPlatformTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    setTemplatesError("");

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const response = await axios.get(
        `${baseURL}/api/platform-settings/service-templates/public?limit=100`,
        { headers },
      );
      setPlatformTemplates(response.data?.templates || []);
    } catch (err) {
      setTemplatesError("Failed to load templates. Please try again.");
    } finally {
      setLoadingTemplates(false);
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  const handleToggleTemplates = () => {
    if (!showTemplates && platformTemplates.length === 0) {
      fetchPlatformTemplates();
    }
    setShowTemplates(!showTemplates);
  };

  const handleToggleTemplateSelection = (templateId) => {
    setSelectedTemplates((prev) =>
      prev.includes(templateId)
        ? prev.filter((id) => id !== templateId)
        : [...prev, templateId],
    );
  };

  const handleBulkAddTemplates = async () => {
    if (selectedTemplates.length === 0 || addingTemplates) return;

    setAddingTemplates(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const templatesToAdd = platformTemplates.filter((template) =>
        selectedTemplates.includes(template._id),
      );

      const existingNames = new Set(
        services.map((svc) => (svc.name || "").toLowerCase().trim()),
      );
      const existingTemplateIds = new Set(
        services
          .map((svc) => svc.platformTemplateId)
          .filter(Boolean)
          .map((id) => id.toString()),
      );

      const duplicateTemplates = templatesToAdd.filter((template) => {
        const normalizedName = (template.name || "").toLowerCase().trim();
        const templateId = template._id?.toString?.() || String(template._id || "");
        return (
          existingNames.has(normalizedName) ||
          existingTemplateIds.has(templateId)
        );
      });

      const uniqueTemplates = templatesToAdd.filter((template) => {
        const normalizedName = (template.name || "").toLowerCase().trim();
        const templateId = template._id?.toString?.() || String(template._id || "");
        return (
          !existingNames.has(normalizedName) &&
          !existingTemplateIds.has(templateId)
        );
      });

      if (uniqueTemplates.length === 0) {
        const duplicateNames = duplicateTemplates.map((template) => template.name);
        setErrorMsg(
          duplicateNames.length > 0
            ? `No new services were added. Duplicate template${duplicateNames.length > 1 ? "s" : ""} skipped: ${duplicateNames.join(", ")}.`
            : "All selected templates already exist in your services.",
        );
        return;
      }

      const addedTemplateNames = [];
      const skippedTemplateNames = duplicateTemplates.map((template) => template.name);
      const failedTemplateNames = [];

      for (const template of uniqueTemplates) {
        const fallbackPrice =
          template.suggestedPrice ?? template.hourlyRate ?? template.maxPrice ?? 1;
        const payload = {
          name: template.name,
          description: template.description || "",
          price: Number(fallbackPrice),
          durationMinutes: Number(template.suggestedDuration || 30),
          category: template.category || "other",
          pricingType: template.pricingType || "fixed",
          platformTemplateId: template._id,
        };

        if (payload.pricingType === "range" && template.maxPrice) {
          payload.maxPrice = Number(template.maxPrice);
        }
        // The same defect as the shop-template copy: a tiered template was copied WITHOUT its options, so
        // the service arrived with pricingType "range" and nothing to choose — unpricesable. Only a few
        // lines away maxPrice/hours were already carried, which is why the gap was easy to miss.
        if (payload.pricingType === "range" && Array.isArray(template.priceTiers)) {
          payload.priceTiers = template.priceTiers;
        }
        if (payload.pricingType === "hourly" && template.hourlyRate) {
          payload.hourlyRate = Number(template.hourlyRate);
        }
        if (payload.pricingType === "variable") {
          payload.requiresConsultation = !!template.requiresConsultation;
        }

        try {
          await axios.post(`${baseURL}/api/staff/services`, payload, { headers });
          addedTemplateNames.push(template.name);
        } catch (err) {
          const errorMessage = err?.response?.data?.message || "";
          const looksLikeDuplicate =
            err?.response?.status === 400 &&
            /duplicate|already exists|already exist/i.test(errorMessage);

          if (looksLikeDuplicate) {
            skippedTemplateNames.push(template.name);
          } else {
            failedTemplateNames.push(template.name);
          }
        }
      }

      await fetchServices();
      setSelectedTemplates([]);
      if (addedTemplateNames.length > 0 || skippedTemplateNames.length > 0) {
        const parts = [];
        if (addedTemplateNames.length > 0) {
          parts.push(
            `${addedTemplateNames.length} service${addedTemplateNames.length > 1 ? "s" : ""} added`,
          );
        }
        if (skippedTemplateNames.length > 0) {
          parts.push(
            `${skippedTemplateNames.length} duplicate${skippedTemplateNames.length > 1 ? "s" : ""} skipped`,
          );
        }
        if (failedTemplateNames.length > 0) {
          parts.push(
            `${failedTemplateNames.length} failed`,
          );
        }

        setSuccessMsg(parts.join(". ") + ".");
        setTimeout(() => setSuccessMsg(""), 5000);
      }

      if (failedTemplateNames.length > 0) {
        setErrorMsg(
          `Some templates could not be added: ${failedTemplateNames.join(", ")}.`,
        );
      }
    } catch (err) {
      setErrorMsg("Unable to add selected templates right now. Please try again.");
    } finally {
      setAddingTemplates(false);
    }
  };

  const handleToggleActive = async (serviceId, currentStatus) => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.patch(
        `${baseURL}/api/staff/services/${serviceId}`,
        { isActive: !currentStatus },
        { headers },
      );

      // Update local state
      setServices(
        services.map((svc) =>
          svc.id === serviceId ? { ...svc, isActive: !currentStatus } : svc,
        ),
      );
    } catch (err) {
      setErrorMsg("Unable to update service status");
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this service?")) {
      return;
    }

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.delete(`${baseURL}/api/staff/services/${serviceId}`, {
        headers,
      });

      // Remove from local state
      setServices(services.filter((svc) => svc.id !== serviceId));
    } catch (err) {
      setErrorMsg("Unable to delete service");
    }
  };

  // Filter services
  const filteredServices = services.filter((service) => {
    const matchesCategory = filter === "all" || service.category === filter;
    const matchesSearch =
      service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (service.description &&
        service.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const filteredPlatformTemplates = platformTemplates.filter((template) => {
    const templateCategory = normalizeCategoryValue(
      getCategoryValue(template?.category),
    );
    const selectedCategory = normalizeCategoryValue(templateCategoryFilter);

    if (selectedCategory !== "all" && templateCategory !== selectedCategory) {
      return false;
    }

    if (!templateSearchTerm) return true;

    const search = templateSearchTerm.toLowerCase();
    return (
      template.name.toLowerCase().includes(search) ||
      (template.description || "").toLowerCase().includes(search)
    );
  });

  const templateCategoryOptions = Array.from(
    new Map(
      platformTemplates
        .filter((template) => template && template.category)
        .map((template) => {
          const rawCategory = getCategoryValue(template.category);
          const normalized = normalizeCategoryValue(rawCategory);
          return [
            normalized,
            { value: normalized, label: toUserFriendly(rawCategory) },
          ];
        }),
    ).values(),
  ).sort((a, b) => a.label.localeCompare(b.label));

  useEffect(() => {
    if (templateCategoryFilter === "all") {
      return;
    }

    const categoryStillExists = platformTemplates.some(
      (template) =>
        normalizeCategoryValue(getCategoryValue(template?.category)) ===
        normalizeCategoryValue(templateCategoryFilter),
    );

    if (!categoryStillExists) {
      setTemplateCategoryFilter("all");
    }
  }, [platformTemplates, templateCategoryFilter]);

  const selectedTemplateObjects = platformTemplates.filter((template) =>
    selectedTemplates.includes(template._id),
  );

  // Handle import services from CSV/Excel
  const handleImportServices = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (!["csv", "xlsx", "xls"].includes(fileExtension)) {
      setErrorMsg("Please upload a CSV or Excel file (.csv, .xlsx, .xls)");
      return;
    }

    try {
      const text = await file.text();
      const lines = text.split("\n").filter((line) => line.trim());

      if (lines.length < 2) {
        setErrorMsg("File must contain a header row and at least one service");
        return;
      }

      // Parse CSV (simple parser - assumes comma-separated values)
      const csvHeaders = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const importedServices = [];

      // Expected columns: name, description, price, duration (or durationMinutes), category
      const nameIndex = csvHeaders.findIndex(
        (h) => h === "name" || h === "service name",
      );
      const descIndex = csvHeaders.findIndex(
        (h) => h === "description" || h === "desc",
      );
      const priceIndex = csvHeaders.findIndex((h) => h === "price");
      const durationIndex = csvHeaders.findIndex(
        (h) =>
          h === "duration" ||
          h === "durationminutes" ||
          h === "duration (minutes)",
      );
      const categoryIndex = csvHeaders.findIndex((h) => h === "category");

      if (nameIndex === -1 || priceIndex === -1 || durationIndex === -1) {
        setErrorMsg("CSV must include columns: name, price, and duration");
        return;
      }

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(",").map((v) => v.trim());

        const name = values[nameIndex];
        const price = parseFloat(values[priceIndex]);
        const duration = parseInt(values[durationIndex]);
        const description = descIndex !== -1 ? values[descIndex] : "";
        const category =
          categoryIndex !== -1
            ? values[categoryIndex]?.toLowerCase() || "other"
            : "other";

        if (name && !isNaN(price) && !isNaN(duration)) {
          importedServices.push({
            name,
            description,
            price,
            durationMinutes: duration,
            category: [
              "haircut",
              "color",
              "styling",
              "treatment",
              "spa",
              "nails",
              "makeup",
              "waxing",
              "other",
            ].includes(category)
              ? category
              : "other",
            isActive: true,
          });
        }
      }

      if (importedServices.length === 0) {
        setErrorMsg("No valid services found in the file");
        return;
      }

      // Send to backend
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const response = await axios.post(
        `${baseURL}/api/staff/services/bulk`,
        {
          services: importedServices,
        },
        { headers },
      );

      if (response.status === 201) {
        alert(
          `Successfully imported ${importedServices.length} service(s)! You can now edit them to add images.`,
        );
        fetchServices(); // Refresh list
        e.target.value = ""; // Reset file input
      }
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.message ||
          "Failed to import services. Please check the file format.",
      );
    }
  };

  if (loading) {
    return (
      <FreelancerSidebarLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading services...</p>
        </div>
      </FreelancerSidebarLayout>
    );
  }

  return (
    <FreelancerSidebarLayout>
      <div
        style={{
          padding: "20px",
          paddingBottom: window.innerWidth < 768 ? "100px" : "60px",
        }}
      >
        {/* Back to Dashboard Button */}
        <button
          onClick={() => navigate(basePath)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            marginBottom: "20px",
            border: "1px solid #ddd",
            borderRadius: "8px",
            background: "#fff",
            color: "#333",
            fontSize: "14px",
            fontWeight: "500",
            cursor: "pointer",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "#f5f5f5";
            e.target.style.borderColor = "#001d4a";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "#fff";
            e.target.style.borderColor = "#ddd";
          }}
        >
          <span>←</span> Back to Dashboard
        </button>

        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "30px",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div style={{ width: "100%" }}>
            <h2 style={{ margin: "0 0 8px 0" }}>Services</h2>
            <div
              style={{
                marginBottom: "14px",
                padding: "12px 14px",
                background: "#eef2ff",
                border: "1px solid #c7d2fe",
                borderRadius: "8px",
                color: "#1e3a8a",
                fontSize: window.innerWidth < 768 ? "12px" : "14px",
                lineHeight: 1.5,
              }}
            >
              Platform templates are ready-made service starters. If you do not
              already have a service, open templates first, select one or more,
              then customize the details after adding them.
            </div>
            <div
              style={{
                display: "flex",
                gap: window.innerWidth < 768 ? "6px" : "10px",
                flexWrap: "nowrap",
              }}
            >
              <button
                className="btn-primary"
                onClick={() => navigate(`${basePath}/services/add`)}
                style={{
                  padding: window.innerWidth < 768 ? "8px 12px" : undefined,
                  fontSize: window.innerWidth < 768 ? "12px" : undefined,
                  whiteSpace: "nowrap",
                }}
              >
                + Add Service
              </button>
              <button
                className="btn-primary"
                style={{
                  background: "#6c757d",
                  padding: window.innerWidth < 768 ? "8px 12px" : undefined,
                  fontSize: window.innerWidth < 768 ? "12px" : undefined,
                  whiteSpace: "nowrap",
                }}
                onClick={() => navigate(`${basePath}/services/bulk-add`)}
              >
                + Bulk Add
              </button>
              <label
                htmlFor="import-csv-input"
                className="btn-primary"
                style={{
                  background: "#10b981",
                  padding: window.innerWidth < 768 ? "8px 12px" : undefined,
                  fontSize: window.innerWidth < 768 ? "12px" : undefined,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  whiteSpace: "nowrap",
                }}
              >
                📥 Import
                <input
                  id="import-csv-input"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => handleImportServices(e)}
                  style={{ display: "none" }}
                />
              </label>
              <button
                className="btn-primary"
                style={{
                  background: showTemplates ? "#6d28d9" : "#4f46e5",
                  padding: window.innerWidth < 768 ? "8px 12px" : undefined,
                  fontSize: window.innerWidth < 768 ? "12px" : undefined,
                  whiteSpace: "nowrap",
                }}
                onClick={handleToggleTemplates}
              >
                {showTemplates ? "Hide Templates" : "Browse Templates"}
              </button>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div
          style={{
            background: "#e6f3ff",
            border: "1px solid #0066cc",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <span style={{ fontSize: "18px" }}>ℹ️</span>
          <p
            style={{
              margin: 0,
              color: "#003d82",
              fontSize: window.innerWidth < 768 ? "12px" : "14px",
            }}
          >
            <strong>Active services</strong> are visible to customers for
            booking. <strong>Deactivated services</strong> are hidden from
            customers.
          </p>
        </div>

        {/* Import Instructions */}
        <div
          style={{
            background: "#f0fdf4",
            border: "1px solid #10b981",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "start",
            gap: "10px",
          }}
        >
          <span style={{ fontSize: "18px" }}>📥</span>
          <div>
            <p
              style={{
                margin: 0,
                color: "#065f46",
                fontSize: "13px",
                fontWeight: "600",
                marginBottom: "4px",
              }}
            >
              Import Services from CSV
            </p>
            <p style={{ margin: 0, color: "#047857", fontSize: "12px" }}>
              Upload a CSV file with columns:{" "}
              <strong>name, price, duration, description, category</strong>.
              Images can be added after import by editing each service.
            </p>
          </div>
        </div>

        {/* Error Message */}
        {errorMsg && (
          <div
            style={{
              background: "#fff3f3",
              border: "1px solid #c00",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#c00" }}>{errorMsg}</p>
          </div>
        )}

        {/* Success Message */}
        {successMsg && (
          <div
            style={{
              background: "#e6ffed",
              border: "1px solid #03543f",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#03543f" }}>{successMsg}</p>
          </div>
        )}

        {showTemplates && (
          <div
            style={{
              marginBottom: "24px",
              padding: "20px",
              background: "linear-gradient(135deg, #f5f7fa 0%, #e4ecff 100%)",
              border: "2px solid #4f46e5",
              borderRadius: "12px",
            }}
          >
            <h3 style={{ margin: "0 0 8px 0", color: "#1f2937" }}>
              Quick Start: Add Services from Platform Templates
            </h3>
            <p style={{ margin: "0 0 14px 0", color: "#4b5563", fontSize: "14px" }}>
              These are starter service templates for freelancers and booth
              renters. Select one or more, then customize the service name,
              price, duration, and details after adding them.
            </p>

            <div
              style={{
                display: "flex",
                gap: "12px",
                marginBottom: "16px",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <input
                type="text"
                placeholder="Search templates..."
                value={templateSearchTerm}
                onChange={(e) => setTemplateSearchTerm(e.target.value)}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
              <select
                value={templateCategoryFilter}
                onChange={(e) => setTemplateCategoryFilter(e.target.value)}
                style={{
                  width: isMobile ? "100%" : "220px",
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  background: "#fff",
                  fontSize: "14px",
                }}
              >
                <option value="all">All Categories</option>
                {templateCategoryOptions.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {loadingTemplates ? (
              <div style={{ textAlign: "center", padding: "24px", color: "#666" }}>
                Loading templates...
              </div>
            ) : templatesError ? (
              <div
                style={{
                  background: "#fff3f3",
                  border: "1px solid #c00",
                  borderRadius: "8px",
                  padding: "12px",
                  color: "#c00",
                }}
              >
                {templatesError}
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "2fr minmax(260px, 1fr)",
                  gap: "16px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "repeat(auto-fill, minmax(230px, 1fr))",
                    gap: "12px",
                    maxHeight: "480px",
                    overflowY: "auto",
                    paddingRight: "4px",
                  }}
                >
                  {filteredPlatformTemplates.map((template) => {
                    const isSelected = selectedTemplates.includes(template._id);
                    return (
                      <div
                        key={template._id}
                        onClick={() => handleToggleTemplateSelection(template._id)}
                        style={{
                          padding: "12px",
                          background: isSelected ? "#eef2ff" : "#fff",
                          border: isSelected ? "2px solid #4f46e5" : "1px solid #ddd",
                          borderRadius: "8px",
                          cursor: "pointer",
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: "10px",
                            right: "10px",
                            width: "20px",
                            height: "20px",
                            borderRadius: "5px",
                            border: isSelected ? "none" : "1px solid #9ca3af",
                            background: isSelected ? "#4f46e5" : "#fff",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            fontWeight: "700",
                          }}
                        >
                          {isSelected ? "✓" : ""}
                        </div>
                        <h4 style={{ margin: "0 0 6px 0", paddingRight: "28px" }}>
                          {template.name}
                        </h4>
                        {template.description && (
                          <p style={{ margin: "0 0 8px 0", fontSize: "12px", color: "#6b7280" }}>
                            {template.description}
                          </p>
                        )}
                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                          <span
                            style={{
                              fontSize: "11px",
                              padding: "2px 8px",
                              background: "#ede9fe",
                              borderRadius: "999px",
                              color: "#5b21b6",
                            }}
                          >
                            {toUserFriendly(template.category)}
                          </span>
                          <span
                            style={{
                              fontSize: "11px",
                              padding: "2px 8px",
                              background: "#dcfce7",
                              borderRadius: "999px",
                              color: "#166534",
                            }}
                          >
                            ${template.suggestedPrice || 0}
                          </span>
                          <span
                            style={{
                              fontSize: "11px",
                              padding: "2px 8px",
                              background: "#dbeafe",
                              borderRadius: "999px",
                              color: "#1d4ed8",
                            }}
                          >
                            {template.suggestedDuration || 30} min
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  {filteredPlatformTemplates.length === 0 && (
                    <div style={{ padding: "24px", color: "#666" }}>
                      No templates available for your current filter. Try a
                      different category, or use these templates as a starting
                      point if you do not already have a service set up.
                    </div>
                  )}
                </div>

                <div
                  className="dashboard-card"
                  style={{
                    padding: "14px",
                    background: "#fff",
                    border: "1px solid #dbe4ff",
                    alignSelf: "start",
                    position: isMobile ? "static" : "sticky",
                    top: "16px",
                  }}
                >
                  <h4 style={{ marginTop: 0, marginBottom: "10px" }}>
                    Selected Services ({selectedTemplateObjects.length})
                  </h4>
                  <div
                    style={{
                      maxHeight: "280px",
                      overflowY: "auto",
                      marginBottom: "12px",
                      border: "1px solid #eee",
                      borderRadius: "8px",
                      padding: "8px",
                      background: "#fafafa",
                    }}
                  >
                    {selectedTemplateObjects.length === 0 ? (
                      <p style={{ margin: 0, color: "#6b7280", fontSize: "13px" }}>
                        Select templates from the left to build a service batch
                        you can customize before publishing.
                      </p>
                    ) : (
                      selectedTemplateObjects.map((template) => (
                        <div
                          key={template._id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "8px",
                            padding: "6px 0",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          <span style={{ fontSize: "13px" }}>{template.name}</span>
                          <button
                            type="button"
                            onClick={() => handleToggleTemplateSelection(template._id)}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: "#dc2626",
                              cursor: "pointer",
                              fontSize: "12px",
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={selectedTemplateObjects.length === 0 || addingTemplates}
                    onClick={handleBulkAddTemplates}
                    className="btn-primary"
                    style={{ width: "100%" }}
                  >
                    {addingTemplates
                      ? "Adding..."
                      : `Add Selected to My Services (${selectedTemplateObjects.length})`}
                  </button>
                  {selectedTemplateObjects.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedTemplates([])}
                      style={{
                        width: "100%",
                        marginTop: "8px",
                        border: "1px solid #d1d5db",
                        background: "#fff",
                        color: "#374151",
                        borderRadius: "8px",
                        padding: "9px",
                        cursor: "pointer",
                      }}
                    >
                      Clear Selection
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div
          style={{
            marginBottom: "20px",
            display: "flex",
            gap: "15px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Search */}
          <input
            type="text"
            placeholder="Search services..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: "1",
              minWidth: "250px",
              padding: "10px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              fontSize: "14px",
            }}
          />

          {/* Category Filter */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              padding: "10px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              fontSize: "14px",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        {/* Services Grid */}
        {filteredServices.length > 0 ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: "20px",
            }}
          >
            {filteredServices.map((service) => (
              <div
                key={service.id}
                className="dashboard-card"
                style={{
                  padding: "20px",
                  position: "relative",
                  border: service.isActive
                    ? "1px solid #e5e7eb"
                    : "2px solid #fbbf24",
                  background: service.isActive ? "#fff" : "#fffbeb",
                }}
              >
                {/* Service Image */}
                {service.serviceImage &&
                  (() => {
                    const baseURL = getApiBaseUrl();
                    // Check if image URL is already absolute (Azure Blob) or relative (local)
                    const isAbsoluteUrl =
                      service.serviceImage.startsWith("http://") ||
                      service.serviceImage.startsWith("https://");
                    const imageUrl = isAbsoluteUrl
                      ? service.serviceImage
                      : baseURL
                        ? `${baseURL}${service.serviceImage}`
                        : "";
                    return imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={service.name}
                        style={{
                          width: "100%",
                          height: "150px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          marginBottom: "15px",
                        }}
                      />
                    ) : null;
                  })()}

                {/* Category Badge */}
                <span
                  style={{
                    display: "inline-block",
                    padding: "4px 12px",
                    background: "#e0e0e0",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: "500",
                    marginBottom: "10px",
                    textTransform: "capitalize",
                  }}
                >
                  {service.category}
                </span>

                {/* Service Details */}
                <h4 style={{ margin: "10px 0" }}>{service.name}</h4>
                {service.description && (
                  <p
                    style={{
                      margin: "8px 0",
                      color: "#666",
                      fontSize: "14px",
                      lineHeight: "1.5",
                    }}
                  >
                    {service.description}
                  </p>
                )}

                {/* Price and Duration */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    margin: "15px 0",
                    padding: "10px 0",
                    borderTop: "1px solid #eee",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <div>
                    <div style={{ fontSize: "12px", color: "#666" }}>Price</div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: "#03543f",
                      }}
                    >
                      ${service.price}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      Duration
                    </div>
                    <div style={{ fontSize: "16px", fontWeight: "500" }}>
                      {service.durationMinutes} min
                    </div>
                  </div>
                </div>

                {/* Status Badge */}
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "6px 14px",
                      background: service.isActive ? "#dcfce7" : "#fed7aa",
                      color: service.isActive ? "#15803d" : "#c2410c",
                      border: service.isActive
                        ? "1px solid #86efac"
                        : "1px solid #fb923c",
                      borderRadius: "20px",
                      fontSize: "13px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {service.isActive ? "✓ Active" : "⚠ Deactivated"}
                  </span>
                  {!service.isActive && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#92400e",
                        marginTop: "8px",
                        fontStyle: "italic",
                      }}
                    >
                      Hidden from customers
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <button
                    onClick={() =>
                      navigate(`${basePath}/services/edit/${service.id}`)
                    }
                    style={{
                      flex: "1",
                      padding: "8px 16px",
                      border: "1px solid #001d4a",
                      borderRadius: "8px",
                      background: "#fff",
                      color: "#001d4a",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() =>
                      handleToggleActive(service.id, service.isActive)
                    }
                    style={{
                      flex: "1",
                      padding: "8px 16px",
                      border: "1px solid #6c757d",
                      borderRadius: "8px",
                      background: "#fff",
                      color: "#6c757d",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                    }}
                  >
                    {service.isActive ? "Deactivate" : "Activate"}
                  </button>
                  <button
                    onClick={() => handleDelete(service.id)}
                    style={{
                      padding: "8px 16px",
                      border: "1px solid #c00",
                      borderRadius: "8px",
                      background: "#fff",
                      color: "#c00",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="dashboard-card"
            style={{ padding: "40px", textAlign: "center" }}
          >
            <p style={{ color: "#666", marginBottom: "20px" }}>
              {searchQuery || filter !== "all"
                ? "No services match your filters"
                : "No services added yet"}
            </p>
            <button
              className="btn-primary"
              onClick={() => navigate(`${basePath}/services/add`)}
            >
              Add New Service
            </button>
          </div>
        )}
      </div>
    </FreelancerSidebarLayout>
  );
}

export default FreelancerServices;
